import { endOfDay, isAfter, isSameDay, startOfDay } from 'date-fns';
import { RRule } from 'shared/lib/rrule';
import { HabitSchedule, ScheduleEntryState } from 'shared/types/habit-schedule';

import { getFrequencyTimeframeCompletions } from '../get-frequency-timeframe-completions';
import { Options } from './types';

const frequencyDue = (
  schedule: HabitSchedule,
  { today, weekStartsOn }: Options,
) => {
  const dayEnd = endOfDay(today);
  // get the entries till the end of today
  const meaningfulEntries = schedule.entries.filter(
    ({ date, state, value }) =>
      !isAfter(date, dayEnd) &&
      ((state === ScheduleEntryState.Complete && !!value) ||
        state !== ScheduleEntryState.Complete),
  );

  const lastEntryDate = meaningfulEntries.length
    ? new Date(Math.max(...meaningfulEntries.map(({ date }) => date.getTime())))
    : undefined;
  if (
    lastEntryDate &&
    (isAfter(lastEntryDate, today) || isSameDay(lastEntryDate, today))
  ) {
    return false;
  }

  const { completionsCount, target } = getFrequencyTimeframeCompletions(
    schedule,
    {
      referenceDate: today,
      weekStartsOn,
      countSkipAsComplete: true,
      countFailAsComplete: true,
    },
  );

  return completionsCount < target;
};

const rruleDue = (schedule: HabitSchedule, { today }: Options) => {
  const dayEnd = endOfDay(today);
  const meaningfulEntries = schedule.entries.filter(
    ({ date, state, value }) =>
      !isAfter(date, dayEnd) &&
      ((state === ScheduleEntryState.Complete &&
        (value ?? 0) >= (schedule.completionTarget?.count ?? 1)) ||
        state !== ScheduleEntryState.Complete),
  );
  const lastEntryDate = meaningfulEntries.length
    ? new Date(Math.max(...meaningfulEntries.map(({ date }) => date.getTime())))
    : undefined;
  if (
    lastEntryDate &&
    (isAfter(lastEntryDate, today) || isSameDay(lastEntryDate, today))
  ) {
    return false;
  }

  // check if day is an occurrence in the rrule
  const rrule = new RRule({
    ...RRule.fromString(schedule.rrule.format).origOptions,
    dtstart: startOfDay(schedule.startDate),
  });
  const [dueOnDay] = rrule.between(startOfDay(today), endOfDay(today), true);

  return !!dueOnDay;
};

export const isScheduleDue = (
  schedule: HabitSchedule,
  { today, weekStartsOn }: Options,
) => {
  if (
    !isSameDay(schedule.startDate, today) &&
    isAfter(schedule.startDate, today)
  ) {
    return false;
  }

  if (schedule.frequency?.count) {
    return frequencyDue(schedule, { today, weekStartsOn });
  }

  return rruleDue(schedule, { today, weekStartsOn });
};
